import React, {
  createContext,
  useState,
  FC,
  ReactNode,
  Dispatch,
  SetStateAction
} from 'react';

import styled from '@emotion/styled';
import { mqMin, LucidTheme } from '@lucid/theme';

import Container from '../container/container';
import NavMenu, { NavMenuProps } from './nav-menu';
import NavLogo, { NavLogoProps } from './nav-logo';
import NavSkipButton from './nav-skip-button';
import useScroll from './use-scroll';

export type ContextUseStateType = Dispatch<SetStateAction<boolean>>[];

export interface NavOptions {
  /** Checks if the navbar is on the top most nav. */
  isTopNav?: boolean;
  /** Checks if the Navbar should be fixed to top of viewport. */
  isSticky?: boolean;
  /** Sets the delay for when the Navbar will show on scrolling. */
  showNavbarOnScrollDelay?: number;
  /** Checks if the Navbar should have a bottom border. */
  willAlwaysHaveBorder?: boolean;
  /** Checks if the Navbar should have a shadow. */
  willAlwaysHaveShadow?: boolean;
  /** Checks if the Navbar should have a bottom border on scroll. */
  willHaveBorderOnScroll?: boolean;
  /** Checks if the Navbar should have a shadow on scroll. */
  willHaveShadowOnScroll?: boolean;
  /** Checks if the Navbar should hide when scrolling. */
  willShowNavbarOnScroll?: boolean;
}

export interface NavProps extends NavOptions {
  /** The children of the Nav component. */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
}

export interface NavSubComponents {
  /** The Nav Context props. */
  Context: typeof NavContext;
  /** The Nav Logo props. */
  Logo: FC<NavLogoProps>;
  /** The Nav Menu props. */
  Menu: FC<NavMenuProps>;
  /** The Site Nav Context props. */
  SiteContext: typeof SiteNavContext;
}

const navContext = {
  /** Checks if the specific dropdown is open. */
  isDropdownExpanded: false,
  /** Checks if the navigation menu is open, that displays the dropdown menu. */
  isMenuOpen: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsDropdownExpanded: (value: SetStateAction<boolean>): void => undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsMenuOpen: (value: SetStateAction<boolean>): void => undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setActiveDropdownCallback: (value: ContextUseStateType): void => undefined
};

const siteNavContext = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setActiveMenuCallback: (value: ContextUseStateType): void => undefined
};

const NavContext = createContext(navContext);
const SiteNavContext = createContext(siteNavContext);

const StyledNav = styled.nav<NavProps & { isMenuOpen?: boolean }>(
  ({
    theme,
    isTopNav,
    isSticky,
    willAlwaysHaveBorder,
    willAlwaysHaveShadow,
    willHaveBorderOnScroll,
    willHaveShadowOnScroll,
  }) => [
    {
      label: 'nav',
      height: '70px',
      background: theme.colors.white,
      position: 'relative',
      width: ['100%', '100vw'],
      transition: 'top 0.2s ease, box-shadow 0.2s ease-out',
      top: 0,
      left: 0
    },
    isTopNav && [
      {
        zIndex: theme.zIndices.z1500,
        backgroundColor: theme.colors.coolGrey[5]
      }
    ],
    !isTopNav && [
      {
        zIndex: theme.zIndices.z1400
      },
      isSticky && {
        [mqMin.tabletLandscape]: {
          position: ['fixed', 'sticky']
        }
      },
      willAlwaysHaveBorder && {
        borderBottom: `1px solid ${theme.colors.coolGrey[20]}`
      },
      willAlwaysHaveShadow && {
        boxShadow: 'rgba(40, 44, 51, 0.2) 0px 4px 4px'
      },
      willHaveBorderOnScroll && {
        borderBottom: `1px solid ${theme.colors.coolGrey[20]}`
      },
      willHaveShadowOnScroll && {
        boxShadow: 'rgba(40, 44, 51, 0.2) 0px 4px 4px',
        transition: 'box-shadow 0.2s ease-out'
      }
    ]
  ]
);

const Nav: FC<NavProps> & NavSubComponents = (
  {
    children,
    className,
    isTopNav = false,
    isSticky = false,
    showNavbarOnScrollDelay = 100,
    willAlwaysHaveBorder = false,
    willAlwaysHaveShadow = false,
    willHaveBorderOnScroll = false,
    willHaveShadowOnScroll = false,
    willShowNavbarOnScroll = false,
    ...rest
  }
) => {
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [[activeDropdownCallback], setActiveDropdownCallback] = useState<ContextUseStateType>([]);
  const [shouldShowNavbarOnScroll, setShouldShowNavbarOnScroll] = useState(false);
  const [willShowBorderOnScroll, setWillShowBorderOnScroll] = useState(false);
  const [willShowShadowOnScroll, setWillShowShadowOnScroll] = useState(false);
  useScroll(({ currentScrollTop, hasScrolledUp }) => {
    const isMinimumScrolled = currentScrollTop >= 70;
    setWillShowBorderOnScroll(currentScrollTop > 2);
    setWillShowShadowOnScroll(currentScrollTop > 2);

    setTimeout(() => {
      setShouldShowNavbarOnScroll(hasScrolledUp && isMinimumScrolled);
    }, showNavbarOnScrollDelay);
  });

  const handleActiveDropdown = (currentActiveDropdown: ContextUseStateType) => {
    const [specificCurrentActiveDropdown] = currentActiveDropdown;

    if (!activeDropdownCallback) {
      return setActiveDropdownCallback(currentActiveDropdown);
    }

    if (activeDropdownCallback !== specificCurrentActiveDropdown) {
      activeDropdownCallback(false);
      return setActiveDropdownCallback(currentActiveDropdown);
    }
  };

  const context = {
    isDropdownExpanded,
    isMenuOpen,
    setIsMenuOpen,
    setIsDropdownExpanded,
    setActiveDropdownCallback: handleActiveDropdown
  };

  return (
    <NavContext.Provider value={context}>
      <StyledNav
        {...rest}
        className={className}
        id={!isTopNav ? 'site-nav' : 'top-nav'}
        isSticky={isSticky}
        isTopNav={isTopNav}
        willAlwaysHaveBorder={willAlwaysHaveBorder}
        willAlwaysHaveShadow={willAlwaysHaveShadow}
        willHaveBorderOnScroll={willHaveBorderOnScroll && willShowBorderOnScroll}
        willHaveShadowOnScroll={willHaveShadowOnScroll && willShowShadowOnScroll}
        willShowNavbarOnScroll={willShowNavbarOnScroll && shouldShowNavbarOnScroll}
      >
        <Container
          displayFlex
          css={(theme: LucidTheme) => [
            {
              [mqMin.tabletLandscape]: {
                justifyContent: 'unset'
              }
            },
            !isTopNav && {
              backgroundColor: theme.colors.white
            },
            isTopNav && {
              backgroundColor: theme.colors.coolGrey[5]
            }
          ]}
        >
          {!isTopNav && (<NavSkipButton />)}
          {children}
        </Container>
      </StyledNav>
    </NavContext.Provider>
  );
};

Nav.Context = NavContext;
Nav.Logo = NavLogo;
Nav.Menu = NavMenu;
Nav.SiteContext = SiteNavContext;

export { Nav };
export default Nav;
